/**
 * 需求来源：docs/旅游落地页/AI提示词.md 第1次提交 需求4（任务单 DEV-120352）；
 *          视觉基准 docs/旅游落地页/cj商旅-01.html 第 4334~4657 行（.cjbt-xp-luxe）与第 4658~4815 行内联脚本。
 * 注意事项：
 * 1. 四态 Editorial selector 完整复刻原型交互：
 *    - 7s 自动轮播（reduce-motion 下不自动播放）；
 *    - 鼠标进入/聚焦暂停，移出/失焦恢复（focusout 需判断 relatedTarget 是否仍在区块内）；
 *    - 键盘 ArrowUp/Down/Left/Right/Home/End 切换并聚焦目标 tab；
 *    - 舞台 pointermove 视差：写 --mx/--my/--move-x/--move-y 四个 CSS 变量，pointerleave 复位。
 * 2. 进度条 is-running 类沿用原型的「移除→强制 reflow→重新添加」写法（void offsetWidth），
 *    否则同一元素上重复添加同名类不会重启 CSS animation。
 * 3. 面板的 is-active 与 aria-selected/tabindex 由 React 依 activeIndex 派生，SSR 首屏即为第 1 态激活，
 *    与原型 activate(0) 后的 DOM 一致。
 * 4. **非激活面板不用 hidden 属性**（DEV-129113 去掉，原型带、此处刻意不跟）：hidden 会让它
 *    display:none、彻底退出布局，于是本块高度只由**当前激活的那一个**面板撑开；四态内容长短不一，
 *    配合 7000ms 自动轮播，整块高度每 7 秒变一次，把下面所有区块（城市视频、Travel Services…）
 *    整体顶上顶下。实测（2026-09-15，预发页 CDP 采样 32s 跑满四态）：本块在 868px ↔ 822px 之间跳，
 *    文档总高 10685 ↔ 10639，落差 46px；去掉 hidden + 面板改网格叠放后，三项波动均为 0px。
 *    隐藏改由 index.less 里既有的 visibility/opacity/pointer-events 承担 —— 它同样把面板移出
 *    无障碍树与 Tab 序列，与 hidden 等价；而且 .cjbt-xp-luxe__panel 上本来就写着
 *    `transition: ... visibility 0.45s ...`，原设计就是交叉淡入淡出，hidden 让淡出那半程从未生效过。
 *    代价：非激活面板的 lazy 图会提前加载（3 张），换来的是切换时不再白闪，可接受。
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ArrowRight } from './Icons';

var AUTOPLAY_INTERVAL = 7000;

export default function Experiences(props) {
  var XP = props.data;
  var tabs = XP.tabs;

  var [activeIndex, setActiveIndex] = useState(0);
  var rootRef = useRef(null);
  var stageRef = useRef(null);
  var tabRefs = useRef([]);
  var timerRef = useRef(null);
  var pausedRef = useRef(false);
  var reduceMotionRef = useRef(false);
  var activeIndexRef = useRef(0);
  var pendingFocusRef = useRef(false);

  activeIndexRef.current = activeIndex;

  /** 复刻原型 restartProgress：清掉所有 is-running，再对当前 tab 强制 reflow 后重新添加，重启进度条动画。 */
  var restartProgress = useCallback(function (index) {
    tabRefs.current.forEach(function (node) {
      if (node) node.classList.remove('is-running');
    });
    var target = tabRefs.current[index];
    if (!reduceMotionRef.current && !pausedRef.current && target) {
      void target.offsetWidth;
      target.classList.add('is-running');
    }
  }, []);

  var startAutoPlay = useCallback(function () {
    if (reduceMotionRef.current) return;
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(function () {
      if (!pausedRef.current) {
        setActiveIndex(function (prev) { return (prev + 1) % tabs.length; });
      }
    }, AUTOPLAY_INTERVAL);
    restartProgress(activeIndexRef.current);
  }, [tabs.length, restartProgress]);

  /** 切换到指定 tab；focusTab=true 时（键盘导航）在渲染后聚焦目标 tab。 */
  var activate = useCallback(function (index, focusTab) {
    var next = (index + tabs.length) % tabs.length;
    pendingFocusRef.current = !!focusTab;
    setActiveIndex(next);
    startAutoPlay();
  }, [tabs.length, startAutoPlay]);

  useEffect(function () {
    if (typeof window === 'undefined') return undefined;
    reduceMotionRef.current = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    startAutoPlay();
    return function () {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [startAutoPlay]);

  // activeIndex 变化后重启进度条动画；键盘导航时补聚焦（原型 activate 内的 tab.focus）
  useEffect(function () {
    restartProgress(activeIndex);
    if (pendingFocusRef.current) {
      pendingFocusRef.current = false;
      var node = tabRefs.current[activeIndex];
      if (node && node.focus) node.focus({ preventScroll: true });
    }
  }, [activeIndex, restartProgress]);

  // 舞台视差：pointermove 写 CSS 变量，pointerleave 复位（reduce-motion 下不启用，与原型一致）
  useEffect(function () {
    var stage = stageRef.current;
    if (!stage || typeof window === 'undefined' || reduceMotionRef.current) return undefined;

    function onPointerMove(event) {
      var rect = stage.getBoundingClientRect();
      var x = ((event.clientX - rect.left) / rect.width) * 100;
      var y = ((event.clientY - rect.top) / rect.height) * 100;
      stage.style.setProperty('--mx', x.toFixed(2) + '%');
      stage.style.setProperty('--my', y.toFixed(2) + '%');
      stage.style.setProperty('--move-x', (((x - 50) / 50) * -7).toFixed(2) + 'px');
      stage.style.setProperty('--move-y', (((y - 50) / 50) * -5).toFixed(2) + 'px');
    }
    function onPointerLeave() {
      stage.style.setProperty('--mx', '74%');
      stage.style.setProperty('--my', '18%');
      stage.style.setProperty('--move-x', '0px');
      stage.style.setProperty('--move-y', '0px');
    }

    stage.addEventListener('pointermove', onPointerMove);
    stage.addEventListener('pointerleave', onPointerLeave);
    return function () {
      stage.removeEventListener('pointermove', onPointerMove);
      stage.removeEventListener('pointerleave', onPointerLeave);
    };
  }, []);

  var pauseAutoPlay = useCallback(function () {
    pausedRef.current = true;
    tabRefs.current.forEach(function (node) {
      if (node) node.classList.remove('is-running');
    });
  }, []);

  var resumeAutoPlay = useCallback(function () {
    pausedRef.current = false;
    startAutoPlay();
  }, [startAutoPlay]);

  var handleKeyDown = useCallback(function (event, index) {
    var nextIndex = index;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      nextIndex = (index + 1) % tabs.length;
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      nextIndex = (index - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = tabs.length - 1;
    } else {
      return;
    }
    event.preventDefault();
    activate(nextIndex, true);
  }, [tabs.length, activate]);

  var counter = String(activeIndex + 1).length < 2 ? '0' + (activeIndex + 1) : String(activeIndex + 1);
  var total = String(tabs.length).length < 2 ? '0' + tabs.length : String(tabs.length);

  return (
    <section
      className="cjbt-xp-luxe"
      id="featured-china-experiences"
      ref={rootRef}
      onMouseEnter={pauseAutoPlay}
      onMouseLeave={resumeAutoPlay}
      onFocus={pauseAutoPlay}
      onBlur={function (event) {
        if (!rootRef.current || !rootRef.current.contains(event.relatedTarget)) resumeAutoPlay();
      }}
    >
      <div className="cjbt-xp-luxe__container">

        <div className="cjbt-xp-luxe__heading">
          <div>
            <div className="cjbt-xp-luxe__eyebrow">{XP.eyebrow}</div>
            <h2 className="cjbt-xp-luxe__title">
              {XP.titleLead} <em>{XP.titleEm}</em>
            </h2>
          </div>
          <p className="cjbt-xp-luxe__intro">{XP.intro}</p>
        </div>

        <div className="cjbt-xp-luxe__console">

          {/* Editorial selector */}
          <div className="cjbt-xp-luxe__rail" role="tablist" aria-label={XP.railLabel}>
            {tabs.map(function (tab, index) {
              var active = index === activeIndex;
              return (
                <button
                  key={tab.id}
                  className={'cjbt-xp-luxe__tab' + (active ? ' is-active' : '')}
                  type="button"
                  role="tab"
                  aria-selected={active ? 'true' : 'false'}
                  aria-controls={'cjbt-xp-panel-' + tab.id}
                  id={'cjbt-xp-tab-' + tab.id}
                  data-cjbt-xp-tab={tab.id}
                  tabIndex={active ? 0 : -1}
                  ref={function (node) { tabRefs.current[index] = node; }}
                  onClick={function () { activate(index, false); }}
                  onKeyDown={function (event) { handleKeyDown(event, index); }}
                >
                  <span className="cjbt-xp-luxe__thumb">
                    <img src={tab.image} alt="" />
                  </span>
                  <span className="cjbt-xp-luxe__tab-copy">
                    <small>{tab.number}</small>
                    <strong>{tab.name}</strong>
                    <span>{tab.tagline}</span>
                  </span>
                  <span className="cjbt-xp-luxe__arrow"><ArrowRight /></span>
                  <span className="cjbt-xp-luxe__tab-progress"></span>
                </button>
              );
            })}
          </div>

          {/* Cinematic stage */}
          <div className="cjbt-xp-luxe__stage-shell">
            <div className="cjbt-xp-luxe__stage" data-cjbt-xp-stage ref={stageRef}>
              <div className="cjbt-xp-luxe__stage-counter" aria-hidden="true">
                <strong data-cjbt-xp-current>{counter}</strong>
                <span>/ {total}</span>
              </div>

              {tabs.map(function (tab, index) {
                var active = index === activeIndex;
                return (
                  <article
                    key={tab.id}
                    className={'cjbt-xp-luxe__panel' + (active ? ' is-active' : '')}
                    id={'cjbt-xp-panel-' + tab.id}
                    role="tabpanel"
                    aria-labelledby={'cjbt-xp-tab-' + tab.id}
                    data-cjbt-xp-panel={tab.id}
                  >
                    <div className="cjbt-xp-luxe__media">
                      <img src={tab.image} alt={tab.mediaAlt} loading="lazy" />
                      <div className="cjbt-xp-luxe__media-copy">
                        <span>{tab.mediaLabel}</span>
                        <strong>{tab.mediaTitle}</strong>
                      </div>
                    </div>

                    <div className="cjbt-xp-luxe__content">
                      <div className="cjbt-xp-luxe__content-top">
                        <span className="cjbt-xp-luxe__number">{tab.number}</span>
                        <span className="cjbt-xp-luxe__category">{tab.category}</span>
                      </div>
                      <h3>{tab.title}</h3>
                      <p>{tab.text}</p>
                      <strong className="cjbt-xp-luxe__subheading">{XP.subheading}</strong>
                      <div className="cjbt-xp-luxe__chips">
                        {tab.chips.map(function (chip, chipIndex) {
                          return <span className="cjbt-xp-luxe__chip" key={chipIndex}>{chip}</span>;
                        })}
                      </div>
                    </div>
                  </article>
                );
              })}

            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
